/**
 * useHousingData Hook
 *
 * Central data provider for the application.
 * - When config.features.useGoogleDrive is true: fetches from the server-side
 *   /api/drive-all-properties endpoint, which caches data in D1 and avoids
 *   Google Drive 403 rate-limit errors by fetching via the server.
 * - Otherwise: uses local mock data
 * - Filters out unavailable properties automatically
 * - Handles loading, error states
 * - Supports cache clearing and refresh
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import config from '../config';
import { clearCache } from '../services/googleDriveService';
import { decrypt } from '../utils/crypto';
import housingData, { getHousingById as getMockHousingById } from '../data/housingData';
import { useTranslation } from 'react-i18next';
import { filterPropertiesBySessionScope } from '../services/sessionService';

// Map English district names from data to Vietnamese names directly
export const DISTRICT_VI_MAP = {
  'District 1': 'Quận 1',
  'District 2': 'Quận 2',
  'District 3': 'Quận 3',
  'District 4': 'Quận 4',
  'District 5': 'Quận 5',
  'District 6': 'Quận 6',
  'District 7': 'Quận 7',
  'District 8': 'Quận 8',
  'District 9': 'Quận 9',
  'District 10': 'Quận 10',
  'District 11': 'Quận 11',
  'District 12': 'Quận 12',
  'Binh Thanh': 'Bình Thạnh',
  'Thu Duc': 'Thủ Đức',
  'Go Vap': 'Gò Vấp',
  'Tan Binh': 'Tân Bình',
  'Tan Phu': 'Tân Phú',
  'Phu Nhuan': 'Phú Nhuận',
  'Ho Chi Minh City': 'TP. Hồ Chí Minh',
  'Da Lat': 'Đà Lạt',
  'Hoi An': 'Hội An',
  'Da Nang': 'Đà Nẵng',
  'Hanoi': 'Hà Nội',
};

/**
 * Force-clear the fully loaded cache so next load fetches fresh data.
 * Call this after admin creates a new property.
 */
export function clearAllCaches() {
  fullyLoadedCache = null;
  clearCache();
  console.log('[useHousingData] All caches cleared');
}

// Map English city names to Vietnamese
const CITY_VI_MAP = {
  'Ho Chi Minh City': 'TP. Hồ Chí Minh',
  'Da Lat': 'Đà Lạt',
  'Hoi An': 'Hội An',
  'Da Nang': 'Đà Nẵng',
  'Hanoi': 'Hà Nội',
};

// In-memory cache for the fully loaded data
let fullyLoadedCache = null;

/**
 * Main hook for consuming housing data throughout the app.
 * Returns filtered list of available properties and helper functions.
 *
 * Uses the server-side /api/drive-all-properties endpoint which:
 * - Reads from D1 cache first (fast, no Drive API calls)
 * - Falls back to Google Drive fetch if cache miss/expired
 * - Stores results in D1 for subsequent requests
 * - Has graceful degradation when D1 daily limits are hit
 */
export function useHousingData() {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dataSource, setDataSource] = useState('mock');
  const [loadedCount, setLoadedCount] = useState(0);
  const mountedRef = useRef(true);
  const { i18n } = useTranslation();

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      if (config.features.useGoogleDrive) {
        // Check for fully cached data first
        if (fullyLoadedCache) {
          if (mountedRef.current) {
            // Apply session scope filtering to cached data
            const filtered = filterPropertiesBySessionScope(fullyLoadedCache);
            setProperties(filtered);
            setLoadedCount(filtered.length);
            setDataSource('google_drive');
            setLoading(false);
          }
          return;
        }

        if (mountedRef.current) {
          setProperties([]);
          setLoadedCount(0);
          setDataSource('google_drive');
        }

        // Fetch all properties from the server-side endpoint
        // This goes through D1 cache -> Google Drive fallback
        const response = await fetch('/api/drive-all-properties');
        
        if (!response.ok) {
          throw new Error(`Server error: ${response.status}`);
        }

        const responseBody = await response.json();
        
        // Handle encrypted responses: server now wraps public responses as { encrypted: "<base64>" }
        // If the response has an "encrypted" field, decrypt it before parsing.
        let data;
        if (responseBody && responseBody.encrypted) {
          const seedKey = config.seedKey;
          const decryptedJson = decrypt(responseBody.encrypted, seedKey);
          data = JSON.parse(decryptedJson);
        } else {
          // Fallback for non-encrypted responses (admin requests, backward compat)
          data = responseBody;
        }
        
        if (data.error) {
          throw new Error(data.error);
        }

        // Convert server format to app format
        // Must match the format produced by propertyParser.js drivePropertyToAppFormat()
        const allProperties = [];
        let index = 0;

        for (const district of data.districts || []) {
          for (const prop of district.properties || []) {
            const titleEn = prop.titleEn || prop.titleVi || prop.name;
            const titleVi = prop.titleVi || prop.name;
            const districtEn = prop.districtEn || prop.districtName || '';
            const districtVi = prop.districtVi || prop.districtName || '';
            // Resolve address fields with the same fallback logic as propertyParser
            const displayAddrEn = prop.displayAddressEn || prop.addressEn || prop.addressVi || '';
            const displayAddrVi = prop.displayAddressVi || prop.addressVi || prop.addressEn || '';
            const addrs = [
              prop.additionalAddress1Vi || prop.additionalAddress1 || '',
              prop.additionalAddress2Vi || prop.additionalAddress2 || '',
              prop.addressVi || prop.addressEn || '',
            ].filter(Boolean);

            allProperties.push({
              id: prop.id || `drive-${index}`,
              driveId: prop.id,
              name: prop.name,
              // EN fields (used by default, HousingCard reads .title, .district, etc.)
              title: titleEn,
              description: prop.descriptionEn || prop.descriptionVi || '',
              displayAddress: displayAddrEn,
              address: displayAddrEn,
              city: prop.cityEn || prop.cityVi || '',
              district: districtEn,
              // VI fields (used when language is vi)
              titleVi,
              descriptionVi: prop.descriptionVi || '',
              displayAddressVi: displayAddrVi,
              addressVi: prop.addressVi || '',
              cityVi: prop.cityVi || '',
              districtVi,
              districtEn,
              // Address fields for geocoding
              additionalAddresses: addrs,
              additionalAddress1: prop.additionalAddress1 || prop.additionalAddress1Vi || '',
              additionalAddress1Vi: prop.additionalAddress1Vi || '',
              additionalAddress2: prop.additionalAddress2 || prop.additionalAddress2Vi || '',
              additionalAddress2Vi: prop.additionalAddress2Vi || '',
              // Price, contact info
              price: prop.price || 0,
              contactPhone: prop.phone || '',
              contactEmail: prop.email || '',
              // Type (must match propertyParser which uses 'type' not 'propertyType')
              type: prop.propertyType || '',
              // Amenities
              amenities: Array.isArray(prop.amenities) ? prop.amenities : [],
              // Images — array of URL strings matching propertyParser.js format
              images: (prop.images || []).map((img) => `/api/drive-image?id=${img.id}`),
              // Videos — array of URL strings
              videos: (prop.videos || []).map((vid) => `/api/drive-image?id=${vid.id}`),
              // Nearby
              nearby: Array.isArray(prop.nearbyEn) ? prop.nearbyEn : (Array.isArray(prop.nearbyVi) ? prop.nearbyVi : []),
              nearbyVi: Array.isArray(prop.nearbyVi) ? prop.nearbyVi : [],
              // Availability
              available: prop.available !== false,
              // Extra fields for compatibility
              featured: false,
              source: 'google_drive',
              drivePropertyId: prop.id,
            });
            index++;
          }
        }

        // Filter out unavailable properties
        const available = allProperties.filter((p) => p.available !== false);
        // Apply session scope filtering (restrict by districts/properties if session has scope)
        const scopedFiltered = filterPropertiesBySessionScope(available);

        if (mountedRef.current) {
          setProperties(scopedFiltered);
          setLoadedCount(scopedFiltered.length);
          // Cache the FULL available set (without scope) so subsequent loads don't re-fetch
          fullyLoadedCache = available;
          console.log(
            `[useHousingData] Loaded ${scopedFiltered.length} properties (${available.length} available) from /api/drive-all-properties`
          );
        }
      } else {
        // Use mock data — instant
        const available = housingData.filter((p) => p.available !== false);
        // Apply session scope filtering
        const scopedFiltered = filterPropertiesBySessionScope(available);

        if (mountedRef.current) {
          setProperties(scopedFiltered);
          setLoadedCount(scopedFiltered.length);
          setDataSource('mock');
          console.log(
            `[useHousingData] Loaded ${scopedFiltered.length} properties from mock data`
          );
        }
      }
    } catch (err) {
      if (mountedRef.current) {
        console.error('[useHousingData] Error loading data:', err);
        setError(err.message || 'Failed to load housing data');

        // Fallback to mock data on error
        const available = housingData.filter((p) => p.available !== false);
        // Apply session scope filtering
        const scopedFiltered = filterPropertiesBySessionScope(available);
        setProperties(scopedFiltered);
        setLoadedCount(scopedFiltered.length);
        setDataSource('mock_fallback');
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    loadData();

    return () => {
      mountedRef.current = false;
    };
  }, [loadData]);

  const refresh = useCallback(() => {
    clearCache();
    fullyLoadedCache = null;
    loadData();
  }, [loadData]);

  const getPropertyById = useCallback(
    (id) => {
      // 1. Check loaded properties first (Google Drive data with string IDs like "drive-1")
      const found = properties.find((p) => p.id === id);
      if (found) return found;

      // 2. Fallback to mock data (supports both numeric IDs like 1 and string IDs)
      const mockProp = getMockHousingById(id);
      if (mockProp && mockProp.available !== false) return mockProp;

      return null;
    },
    [properties]
  );

  const getLocations = useCallback(() => {
    const isVietnamese = i18n.language === 'vi';
    const locs = [
      ...new Set(properties.map((item) => {
        if (isVietnamese) {
          return DISTRICT_VI_MAP[item.district] || item.districtVi || item.district;
        }
        return item.district;
      })),
    ];
    return locs.sort((a, b) => a.localeCompare(b, 'vi'));
  }, [properties, i18n.language]);

  const getCities = useCallback(() => {
    const isVietnamese = i18n.language === 'vi';
    const cities = [
      ...new Set(properties.map((item) => {
        if (isVietnamese) {
          return CITY_VI_MAP[item.city] || item.cityVi || item.city;
        }
        return item.city;
      })),
    ];
    return cities.sort((a, b) => a.localeCompare(b, 'vi'));
  }, [properties, i18n.language]);

  return {
    properties,
    loading,
    error,
    dataSource,
    loadedCount,
    refresh,
    getPropertyById,
    getLocations,
    getCities,
  };
}

/**
 * Hook that only provides a single property by ID.
 * Useful for the detail page to avoid re-rendering the full list.
 */
export function useHousingProperty(id) {
  const { getPropertyById, dataSource } = useHousingData();

  if (dataSource === 'mock' || dataSource === 'mock_fallback') {
    const mockProp = getMockHousingById(id);
    if (mockProp && mockProp.available !== false) return mockProp;
    return null;
  }

  return getPropertyById(id);
}

/**
 * Get the raw mock data (for direct access when needed)
 */
export function getAvailableMockData() {
  return housingData.filter((p) => p.available !== false);
}

export default useHousingData;